import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { CptModel } from '@/models/cpt'
import locationModel from '@/models/location'
import trainerModel from '@/models/trainer'
import type { Locale } from '@/router/locales'
import { ContentService, type ContentEtags } from '@/services/content.service'
import { useLocaleStore } from '@/stores/locale.store'
import { sortItems } from '@/utils/sortItems'

export type HeaderNavigationItem = {
  id: number
  title: string
  slug: string
  relativeUrl?: string
  description?: string
}

export type HeaderNavigationSnapshot = {
  gyms: HeaderNavigationItem[]
  trainers: HeaderNavigationItem[]
  etags: Pick<ContentEtags, 'gyms' | 'trainers'>
  locale: Locale
}

function compactGyms(items: CptModel[]): HeaderNavigationItem[] {
  return sortItems('menuOrder')(items.map(locationModel)).map((item) => ({
    id: item.id,
    title: item.title,
    slug: item.slug,
    relativeUrl: item.relativeUrl,
    description: [item.address?.address, item.address?.postalCode, item.address?.city]
      .filter(Boolean)
      .join(', ') || undefined,
  }))
}

function compactTrainers(items: CptModel[]): HeaderNavigationItem[] {
  return sortItems('menuOrder')(items.map(trainerModel)).map((item) => ({
    id: item.id,
    title: item.person?.name || item.title,
    slug: item.slug,
    relativeUrl: item.relativeUrl,
    description: [
      item.person?.level,
      item.person?.locations.map((location) => location.name).filter(Boolean).join(', ')
        || undefined,
    ].filter(Boolean).join(' · ') || undefined,
  }))
}

export function createHeaderNavigationSnapshot(
  gyms: CptModel[],
  trainers: CptModel[],
  etags: ContentEtags,
  locale: Locale,
): HeaderNavigationSnapshot {
  return {
    gyms: compactGyms(gyms),
    trainers: compactTrainers(trainers),
    etags: {
      ...(etags.gyms !== undefined ? { gyms: etags.gyms } : {}),
      ...(etags.trainers !== undefined ? { trainers: etags.trainers } : {}),
    },
    locale,
  }
}

export const useHeaderNavigationStore = defineStore('headerNavigation', () => {
  const localeStore = useLocaleStore()
  const gyms = ref<HeaderNavigationItem[]>([])
  const trainers = ref<HeaderNavigationItem[]>([])
  const etags = ref<Pick<ContentEtags, 'gyms' | 'trainers'>>({})
  const locale = ref<Locale | null>(null)
  const isHydrated = ref(false)

  function hydrate(snapshot: HeaderNavigationSnapshot) {
    gyms.value = snapshot.gyms
    trainers.value = snapshot.trainers
    etags.value = snapshot.etags
    locale.value = snapshot.locale
    isHydrated.value = true
  }

  async function revalidate() {
    const sameLocale = locale.value === localeStore.currentLocale
    const [gymResult, trainerResult] = await Promise.allSettled([
      ContentService.gyms(sameLocale ? etags.value.gyms : undefined),
      ContentService.trainers(sameLocale ? etags.value.trainers : undefined),
    ])

    if (gymResult.status === 'fulfilled') {
      if (!gymResult.value.notModified) {
        gyms.value = compactGyms(gymResult.value.data)
      }

      if (gymResult.value.etag === null) {
        delete etags.value.gyms
      } else {
        etags.value.gyms = gymResult.value.etag
      }
    }

    if (trainerResult.status === 'fulfilled') {
      if (!trainerResult.value.notModified) {
        trainers.value = compactTrainers(trainerResult.value.data)
      }

      if (trainerResult.value.etag === null) {
        delete etags.value.trainers
      } else {
        etags.value.trainers = trainerResult.value.etag
      }
    }

    locale.value = localeStore.currentLocale
    isHydrated.value = true
  }

  return {
    gyms,
    trainers,
    etags,
    locale,
    isHydrated,
    hydrate,
    revalidate,
  }
})

